package org.vreo.devicehub.features.deviceDetails.presentation

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Devices
import androidx.compose.material.icons.filled.EditNote
import androidx.compose.material.icons.filled.History
import androidx.compose.material.icons.filled.LinkOff
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.Numbers
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Router
import androidx.compose.material.icons.filled.Sell
import androidx.compose.material.icons.filled.SettingsEthernet
import androidx.compose.material.icons.filled.SystemUpdate
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material.icons.filled.Wifi
import androidx.compose.material.icons.filled.WifiOff
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.ListItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.core.presentation.components.sectionHeader.SectionHeader
import org.vreo.devicehub.features.core.domain.device.device.ProductType
import org.vreo.devicehub.features.device.presentation.components.toUiText

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.cancel_button_title
import devicehub.composeapp.generated.resources.confirm_button_title
import devicehub.composeapp.generated.resources.device_delete_confirm_dialog
import devicehub.composeapp.generated.resources.device_details_general_section_label
import devicehub.composeapp.generated.resources.device_details_id_label
import devicehub.composeapp.generated.resources.device_details_last_activity_label
import devicehub.composeapp.generated.resources.device_details_linking_password
import devicehub.composeapp.generated.resources.device_details_local_ip_address_label
import devicehub.composeapp.generated.resources.device_details_mac_address_label
import devicehub.composeapp.generated.resources.device_details_management_section_label
import devicehub.composeapp.generated.resources.device_details_model_label
import devicehub.composeapp.generated.resources.device_details_network_section_label
import devicehub.composeapp.generated.resources.device_details_not_available_label
import devicehub.composeapp.generated.resources.device_details_screen_title
import devicehub.composeapp.generated.resources.device_details_security_section_label
import devicehub.composeapp.generated.resources.device_details_serial_number_label
import devicehub.composeapp.generated.resources.device_details_software_section_label
import devicehub.composeapp.generated.resources.device_details_status_active_label
import devicehub.composeapp.generated.resources.device_details_status_inactive_label
import devicehub.composeapp.generated.resources.device_details_status_label
import devicehub.composeapp.generated.resources.device_details_tag_label
import devicehub.composeapp.generated.resources.device_details_unlink_device_label
import devicehub.composeapp.generated.resources.device_menu_item_delete_label
import devicehub.composeapp.generated.resources.device_unlink_confirm_dialog
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.message_details_ocr_version_title
import devicehub.composeapp.generated.resources.message_details_software_version_title
import devicehub.composeapp.generated.resources.refresh_button_title
import devicehub.composeapp.generated.resources.save_button_title

/**
 * Stateful entry point for the device details screen. Collects [DeviceDetailsViewModel.state]
 * and forwards UI actions back to the view model.
 *
 * @param viewModel view model providing state and handling actions
 */
@Composable
fun DeviceDetailsScreenRoot(
    viewModel: DeviceDetailsViewModel,
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    DeviceDetailsScreenView(
        state = state,
        isAdmin = viewModel.isAdmin,
        onAction = { action -> viewModel.onAction(action) }
    )
}

/**
 * Device details screen view. Presents a read-only overview of the selected device, backed
 * entirely by [DeviceDetailsState] - the domain model is never accessed from the view.
 *
 * @param state state to present
 * @param scrollState scroll state
 */
@Composable
fun DeviceDetailsScreenView(
    state: DeviceDetailsState,
    isAdmin: Boolean,
    onAction: (DeviceDetailsActions) -> Unit = {},
    scrollState: ScrollState = rememberScrollState(),
) {
    // Handling Dialogs
    if (state.isLoading) LoadingDialog()
    state.error?.let { error ->
        ErrorDialog(
            dialogTitle = stringResource(Res.string.error_general_title),
            dialogText = error.asString(),
            onDismissRequest = { onAction(DeviceDetailsActions.OnClearError) },
            onConfirmation = { onAction(DeviceDetailsActions.OnClearError) }
        )
    }

    val notAvailable = stringResource(Res.string.device_details_not_available_label)

    Column(
        modifier = Modifier
            .fillMaxHeight()
            .widthIn(max = 1400.dp)
            .verticalScroll(scrollState)
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(20.dp)
    ) {
        // Screen title with icon
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Icon(
                imageVector = Icons.Default.Devices,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(28.dp)
            )
            Text(
                text = stringResource(Res.string.device_details_screen_title),
                style = MaterialTheme.typography.headlineSmall,
            )
        }

        FilledTonalButton(
            onClick = { onAction(DeviceDetailsActions.Refresh) },
            modifier = Modifier.fillMaxWidth()
        ) {
            Icon(
                imageVector = Icons.Default.Refresh,
                contentDescription = null,
                modifier = Modifier.size(ButtonDefaults.IconSize)
            )
            Spacer(Modifier.width(ButtonDefaults.IconSpacing))
            Text(stringResource(Res.string.refresh_button_title))
        }

        // Device identity & connection status
        OutlinedCard(
            shape = MaterialTheme.shapes.medium,
            modifier = Modifier.fillMaxWidth()
        ) {
            ListItem(
                leadingContent = {
                    Icon(
                        imageVector = if (state.isDeviceActive) Icons.Default.Wifi else Icons.Default.WifiOff,
                        contentDescription = null,
                        tint = if (state.isDeviceActive) MaterialTheme.colorScheme.primary
                        else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                },
                headlineContent = {
                    Text(state.userTag?.ifBlank { null } ?: state.deviceId)
                },
                supportingContent = {
                    Text(stringResource(Res.string.device_details_status_label))
                },
                trailingContent = {
                    StatusBadge(isActive = state.isDeviceActive)
                }
            )
        }

        // General information
        DetailsSection(
            icon = { Icon(Icons.Default.Devices, contentDescription = null) },
            title = stringResource(Res.string.device_details_general_section_label)
        ) {
            DetailItem(
                icon = Icons.Default.Numbers,
                label = stringResource(Res.string.device_details_id_label),
                value = state.deviceId.ifBlank { notAvailable },
            )
            DetailItem(
                icon = Icons.Default.Devices,
                label = stringResource(Res.string.device_details_model_label),
                value = state.productType?.toUiText()?.asString() ?: notAvailable,
            )
            DetailItem(
                icon = Icons.Default.Numbers,
                label = stringResource(Res.string.device_details_serial_number_label),
                value = state.serialNumber.ifBlank { notAvailable },
            )

            AnimatedContent(targetState = state.editingTag) { editing ->
                if (editing) {
                    ListItem(
                        headlineContent = {
                            Column(
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxWidth(),
                            ) {
                                OutlinedTextField(
                                    singleLine = true,
                                    value = state.temporarilyTag,
                                    label = { Text(stringResource(Res.string.device_details_tag_label)) },
                                    onValueChange = {
                                        onAction(DeviceDetailsActions.OnChangeEditTag(it))
                                    },
                                    shape = MaterialTheme.shapes.small,
                                    modifier = Modifier.fillMaxWidth(),
                                )

                                Button(
                                    onClick = {
                                        onAction(DeviceDetailsActions.OnSaveTag)
                                    },
                                    modifier = Modifier.fillMaxWidth(),
                                ) {
                                    Text(stringResource(Res.string.save_button_title))
                                }

                                FilledTonalButton(
                                    onClick = {
                                        onAction(DeviceDetailsActions.OnChangeEditTagEnable(false))
                                    },
                                    modifier = Modifier.fillMaxWidth(),
                                ) {
                                    Text(stringResource(Res.string.cancel_button_title))
                                }
                            }
                        }
                    )
                } else {
                    DetailItem(
                        icon = Icons.Default.Sell,
                        label = stringResource(Res.string.device_details_tag_label),
                        value = state.userTag?.ifBlank { null } ?: notAvailable,
                        tailingContent = {
                            IconButton(
                                onClick = {
                                    onAction(DeviceDetailsActions.OnChangeEditTagEnable(!editing))
                                },
                            ) {
                                Icon(
                                    imageVector = Icons.Outlined.Edit,
                                    contentDescription = null
                                )
                            }
                        }
                    )
                }
            }
        }

        // Network
        DetailsSection(
            icon = { Icon(Icons.Default.Router, contentDescription = null) },
            title = stringResource(Res.string.device_details_network_section_label)
        ) {
            MaskedDetailItem(
                icon = Icons.Default.Router,
                label = stringResource(Res.string.device_details_local_ip_address_label),
                value = state.localIpAddress,
                notAvailableText = notAvailable,
            )
            MaskedDetailItem(
                icon = Icons.Default.SettingsEthernet,
                label = stringResource(Res.string.device_details_mac_address_label),
                value = state.macAddress,
                notAvailableText = notAvailable,
            )
            DetailItem(
                icon = Icons.Default.History,
                label = stringResource(Res.string.device_details_last_activity_label),
                value = state.lastActivityTime ?: notAvailable,
            )
        }

        // Software
        DetailsSection(
            icon = { Icon(Icons.Default.SystemUpdate, contentDescription = null) },
            title = stringResource(Res.string.device_details_software_section_label)
        ) {
            DetailItem(
                icon = Icons.Default.SystemUpdate,
                label = stringResource(Res.string.message_details_software_version_title),
                value = state.softwareVersion.ifBlank { notAvailable },
            )
            DetailItem(
                icon = Icons.Default.SystemUpdate,
                label = stringResource(Res.string.message_details_ocr_version_title),
                value = state.ocrVersion.ifBlank { notAvailable },
            )
        }

        // Security - only shown when the device exposes a linking password
        state.linkingPassword?.let { password ->
            DetailsSection(
                icon = { Icon(Icons.Default.Lock, contentDescription = null) },
                title = stringResource(Res.string.device_details_security_section_label)
            ) {
                MaskedDetailItem(
                    icon = Icons.Default.Lock,
                    label = stringResource(Res.string.device_details_linking_password),
                    value = password,
                    notAvailableText = notAvailable,
                )
            }
        }

        // Management
        DetailsSection(
            icon = { Icon(Icons.Default.EditNote, contentDescription = null) },
            title = stringResource(Res.string.device_details_management_section_label)
        ) {
            ListItem(
                leadingContent = {
                    Icon(
                        imageVector = Icons.Default.LinkOff,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                },
                headlineContent = {
                    FilledTonalButton(
                        onClick = {
                            onAction(DeviceDetailsActions.OnUnlinkClicked)
                        },
                        modifier = Modifier
                            .widthIn(max = 400.dp)
                            .fillMaxWidth()
                            .align(Alignment.CenterHorizontally),
                    ) {
                        Text(stringResource(Res.string.device_details_unlink_device_label))
                    }
                },
                modifier = Modifier.fillMaxWidth(),
            )

            if (isAdmin) {
                ListItem(
                    leadingContent = {
                        Icon(
                            imageVector = Icons.Default.Delete,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    },
                    headlineContent = {
                        FilledTonalButton(
                            onClick = {
                                onAction(DeviceDetailsActions.OnDeleteClicked)
                            },
                            modifier = Modifier
                                .widthIn(max = 400.dp)
                                .fillMaxWidth()
                                .align(Alignment.CenterHorizontally),
                        ) {
                            Text(stringResource(Res.string.device_menu_item_delete_label))
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }

        // Unlink alert dialog
        AnimatedVisibility(
            visible = state.showUnlinkDialog,
        ) {
            AlertDialog(
                icon = {
                    Icon(Icons.Default.LinkOff, contentDescription = null)
                },
                title = {
                    Text(stringResource(Res.string.device_details_unlink_device_label))
                },
                text = {
                    Text(stringResource(Res.string.device_unlink_confirm_dialog) + "\n\n" + state.deviceId)
                },
                onDismissRequest = {
                    onAction(DeviceDetailsActions.OnClearUnlinkDialog)
                },
                dismissButton = {
                    TextButton(
                        onClick = {
                            onAction(DeviceDetailsActions.OnClearUnlinkDialog)
                        }
                    ) {
                        Text(stringResource(Res.string.cancel_button_title))
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            onAction(DeviceDetailsActions.OnUnlinkDeviceConfirmed)
                        }
                    ) {
                        Text(stringResource(Res.string.confirm_button_title))
                    }
                },
            )
        }

        // Delete alert dialog
        AnimatedVisibility(
            visible = state.showDeleteDialog && isAdmin,
        ) {
            AlertDialog(
                icon = {
                    Icon(Icons.Default.Delete, contentDescription = null)
                },
                title = {
                    Text(stringResource(Res.string.device_menu_item_delete_label))
                },
                text = {
                    Text(stringResource(Res.string.device_delete_confirm_dialog) + "\n\n" + state.deviceId)
                },
                onDismissRequest = {
                    onAction(DeviceDetailsActions.OnClearDeleteDialog)
                },
                dismissButton = {
                    TextButton(
                        onClick = {
                            onAction(DeviceDetailsActions.OnClearDeleteDialog)
                        }
                    ) {
                        Text(stringResource(Res.string.cancel_button_title))
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            onAction(DeviceDetailsActions.OnDeleteConfirmed)
                        }
                    ) {
                        Text(stringResource(Res.string.confirm_button_title))
                    }
                },
            )
        }
    }
}

/**
 * Outlined card wrapping a titled group of [DetailItem]s, matching the section styling used
 * across device management screens.
 *
 * @param icon section icon
 * @param title section title
 * @param content section body, typically a series of [DetailItem]
 */
@Composable
private fun DetailsSection(
    icon: @Composable () -> Unit,
    title: String,
    content: @Composable () -> Unit,
) {
    OutlinedCard(
        shape = MaterialTheme.shapes.medium,
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(vertical = 8.dp)) {
            Row(modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)) {
                SectionHeader(icon = icon, title = title)
            }
            HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))
            content()
        }
    }
}

/**
 * Single read-only device detail row: an icon, a small overline label and the value beneath it.
 * The value is wrapped in a [SelectionContainer] so identifiers/keys can be copied.
 *
 * @param icon leading icon
 * @param label field label
 * @param value field value
 * @param tailingContent tailing content
 */
@Composable
private fun DetailItem(
    icon: ImageVector,
    label: String,
    value: String,
    tailingContent: (@Composable () -> Unit)? = null,
) {
    ListItem(
        leadingContent = {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        },
        overlineContent = {
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        },
        headlineContent = {
            SelectionContainer {
                Text(
                    text = value,
                    style = MaterialTheme.typography.bodyLarge
                )
            }
        },
        trailingContent = tailingContent
    )
}

/**
 * Read-only detail row for sensitive values (passwords, IP/MAC addresses). The value is masked
 * as "******" by default and can be revealed via the trailing toggle button. When [value] is
 * blank, [notAvailableText] is shown instead and no toggle is displayed, since there is nothing
 * to reveal.
 *
 * @param icon leading icon
 * @param label field label
 * @param value sensitive value to mask/reveal
 * @param notAvailableText text shown in place of an empty [value]
 */
@Composable
private fun MaskedDetailItem(
    icon: ImageVector,
    label: String,
    value: String,
    notAvailableText: String,
) {
    var isVisible by remember { mutableStateOf(false) }
    val hasValue = value.isNotBlank()

    ListItem(
        leadingContent = {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        },
        overlineContent = {
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        },
        headlineContent = {
            SelectionContainer {
                Text(
                    text = when {
                        !hasValue -> notAvailableText
                        isVisible -> value
                        else -> "******"
                    },
                    style = MaterialTheme.typography.bodyLarge
                )
            }
        },
        trailingContent = if (hasValue) {
            {
                IconButton(onClick = { isVisible = !isVisible }) {
                    Icon(
                        imageVector = if (isVisible) Icons.Default.VisibilityOff else Icons.Default.Visibility,
                        contentDescription = null
                    )
                }
            }
        } else null
    )
}

/**
 * Pill-shaped connection status indicator.
 *
 * @param isActive whether the device is currently active
 */
@Composable
private fun StatusBadge(isActive: Boolean) {
    val containerColor = if (isActive) MaterialTheme.colorScheme.primaryContainer
    else MaterialTheme.colorScheme.errorContainer
    val contentColor = if (isActive) MaterialTheme.colorScheme.onPrimaryContainer
    else MaterialTheme.colorScheme.onErrorContainer

    Surface(
        color = containerColor,
        shape = MaterialTheme.shapes.extraLarge,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp)
        ) {
            Icon(
                imageVector = if (isActive) Icons.Default.Wifi else Icons.Default.WifiOff,
                contentDescription = null,
                tint = contentColor,
                modifier = Modifier.size(16.dp)
            )
            Text(
                text = stringResource(
                    if (isActive) Res.string.device_details_status_active_label
                    else Res.string.device_details_status_inactive_label
                ),
                style = MaterialTheme.typography.labelMedium,
                color = contentColor
            )
        }
    }
}

/** Preview of [DeviceDetailsScreenView] with sample device data. */
@Preview
@Composable
fun DeviceDetailsScreenViewPreview() {
    MaterialTheme {
        Scaffold {
            DeviceDetailsScreenView(
                state = DeviceDetailsState(
                    isDeviceActive = true,
                    deviceId = "volt-s1-pole-short-range-0001",
                    productType = ProductType.VoltS1PoleShortRange,
                    serialNumber = "0001",
                    softwareVersion = "1.2.3",
                    ocrVersion = "1.2.3",
                    localIpAddress = "192.168.1.1",
                    macAddress = "11:22:33:44:55:66",
                    lastActivityTime = "01-09-2026  10:04",
                    userTag = "Entrance camera",
                    linkingPassword = "hunter2",
                ),
                isAdmin = false
            )
        }
    }
}
